/**
 * Chekni chop etish va Telegram orqali ulashish.
 *
 * Ikkala yo'l ham bitta joyda turadi, chunki ular yuzaga (Telegram/brauzer)
 * qarab boshqacha ishlaydi:
 *
 *   - Telegram WebView'da `window.open` ham, `window.print()` ham ishlamaydi.
 *     Shuning uchun server imzolangan (1 soat yashaydigan) havola beradi va
 *     u `WebApp.openLink()` bilan TASHQI brauzerda ochiladi — print dialogi
 *     o'sha yerda chiqadi.
 *   - Brauzerda oyna bosish ishorasi hali "issiq" paytda ochiladi; so'rov
 *     tugaganidan keyin ochilsa popup bloker uni to'sadi (aynan shu sabab
 *     "Brauzer yangi oynani to'sdi" xatosi chiqardi).
 */
import { ordersApi } from '../api';
import { isTelegram } from './surface';

/**
 * Telegram SDK — `index.html` yuklagan skriptdan olinadi.
 *
 * `@twa-dev/sdk` paketi o'z ichida skriptning nusxasini olib yuradi va uni
 * import paytida ishga tushiradi; ikkita nusxa bir-birini bosib ketmasin
 * deb bu yerda global obyektga tayanamiz (`app/surface.ts` ham shunday).
 */
function webApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

/** Telegram'ning "ulashish" oynasi (Mini Apps 8.0) shu klientda bormi. */
export function canShareToTelegram(): boolean {
  try {
    const tg = webApp();
    return Boolean(
      isTelegram()
      && tg?.isVersionAtLeast?.('8.0')
      && typeof tg.shareMessage === 'function',
    );
  } catch {
    return false;
  }
}

/** Foydalanuvchiga ko'rsatsa bo'ladigan xato (server javobi emas, o'zimizniki). */
class ReceiptError extends Error {}

/** Xatoni ekranga chiqariladigan matnga aylantirish. */
export function receiptError(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (data?.error || data?.detail) return data.error || data.detail;
  if (err instanceof ReceiptError) return err.message;
  return fallback;
}

/**
 * Chekni chop etish sahifasini ochish.
 *
 * MUHIM: bu funksiya to'g'ridan-to'g'ri `onClick` ichidan chaqirilishi kerak —
 * brauzer oynasi so'rovdan oldin, ishora hali amal qilib turganda ochiladi.
 */
export async function openReceiptPrint(orderId: number): Promise<void> {
  const inTelegram = isTelegram();
  const win = inTelegram ? null : window.open('', '_blank');
  try {
    const res = await ordersApi.printLink(orderId);
    const url = res.data?.url;
    if (!url) throw new ReceiptError('Chop etish havolasi kelmadi.');

    const tg = webApp();
    if (inTelegram && tg?.openLink) {
      tg.openLink(url, { try_instant_view: false });
    } else if (win) {
      win.location.href = url;
    } else {
      // Bloker baribir to'sdi — chekni shu oynada ochamiz (orqaga qaytish bor).
      window.location.href = url;
    }
  } catch (err) {
    win?.close();
    throw err;
  }
}

/**
 * Chek PDF'ini Telegram orqali ulashish.
 *
 * Server PDF'ni botga yuklab, tayyor xabar (`prepared_message_id`) qaytaradi;
 * Telegram esa chat tanlash oynasini ochadi.
 *
 * Oyna ochilgach darhol qaytamiz: `shareMessage` callback'i ba'zi klientlarda
 * umuman chaqirilmaydi, unga qarab turilsa tugma osilib qolardi.
 */
export async function shareReceiptToTelegram(
  orderId: number,
  onResult?: (sent: boolean) => void,
): Promise<void> {
  if (!canShareToTelegram()) {
    throw new ReceiptError('Bu Telegram versiyasi ulashishni qo\'llamaydi.');
  }
  const res = await ordersApi.shareTelegram(orderId);
  const messageId = res.data?.prepared_message_id;
  if (!messageId) throw new ReceiptError('Ulashish xabari tayyorlanmadi.');
  webApp()!.shareMessage!(String(messageId), (sent) => onResult?.(Boolean(sent)));
}
